import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileArrowUp, DownloadSimple, Trash, FilePdf, File, LockSimple } from "@phosphor-icons/react";

const CATEGORIES = [
  { v: "master_cv", l: "Master CV" },
  { v: "cv_version", l: "CV version" },
  { v: "cover_letter", l: "Cover letter" },
  { v: "certificate", l: "Certificate" },
  { v: "diploma", l: "Diploma" },
  { v: "reference", l: "Work reference" },
  { v: "portfolio", l: "Portfolio / work sample" },
  { v: "other", l: "Other" },
];

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("master_cv");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/documents").then((r) => setDocs(r.data.documents));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    fd.append("label", file.name);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded — private to you");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/documents/${id}`); toast("Document removed"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div>
      <PageHeader title="Document vault" subtitle="Private to your account. Documents are never shown to employers unless you attach them to an application." />

      <div className="rounded-lg border border-slate-200 bg-white p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="sm:w-64">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1.5" data-testid="doc-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="doc-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-doc-btn" className="bg-slate-900 hover:bg-slate-800 gap-2">
            <FileArrowUp weight="fill" /> {uploading ? "Uploading…" : "Upload document"}
          </Button>
        </div>
        <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5"><LockSimple /> Stored privately. PDF, Word, and text files supported.</p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="documents-list">
        {docs.length === 0 && <p className="p-8 text-sm text-slate-500 text-center">No documents yet.</p>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-3 px-5 py-3.5 border-b border-slate-100 last:border-0" data-testid={`doc-${d.id}`}>
            <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center text-slate-500">
              {d.content_type?.includes("pdf") ? <FilePdf size={20} /> : <File size={20} />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-slate-900 truncate">{d.label}</p>
              <p className="text-xs text-slate-500">{CATEGORIES.find((c) => c.v === d.category)?.l || d.category} · {(d.size / 1024).toFixed(0)} KB</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => download(d)} className="text-slate-500" data-testid={`download-${d.id}`}><DownloadSimple /></Button>
            <Button variant="ghost" size="icon" onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-600" data-testid={`delete-doc-${d.id}`}><Trash /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}
